import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import log from 'loglevel';
import authService, { privileges } from '@/shared/services/AuthService';
import moment from 'moment';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FaCog, FaInfoCircle } from 'react-icons/fa';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import Popover from 'react-bootstrap/lib/Popover';
import intlShape from '@/shared/utils/intlShape';
import { EyeIcon, ListIcon } from '@/shared/components/Icons';
import Button from '@/shared/components/Button';
import ForwardEmailModal from '@/shared/components/email/ForwardEmailModal';
import DocumentService from '@/shared/services/DocumentService';
import enumCache from '@/shared/services/EnumCacheService';

import api from '@/shared/api';
import ToastrService from '@/shared/services/ToastrService';
import ScrollableTable from '@/shared/components/table/ScrollableTable';
import ColumnHeader from '@/shared/components/table/ColumnHeader';
import SimplePanel from '@/shared/components/SimplePanel';
import { CanCreate, showWhenPermission } from '@/shared/components/security';
import SignalRService from '@/shared/services/SignalRService';
import DocumentTreeView from '@/shared/components/DocumentTreeView';
import withDropZone from '@/shared/components/dragAndDrop/withDropzone';
import { injectStateService } from '@/shared/services/StateService';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import handleHttpError from '@/shared/services/HandleHttpError';
import Checkbox from '@/shared/components/input/Checkbox';
import YesNoModal from '@/shared/components/YesNoModal';
import SingleRelationMailModal from '@/shared/components/email/SingleRelationMailModal';
import userStorage from '@/shared/services/userStorage';
import DataCacheService from '@/shared/services/DataCacheService';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import ShareItemModal from '@/shared/components/ShareItemModal';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import {
  handleHiddenColumnsSelection,
  initHiddenColumnsState,
} from '@/shared/utils/hiddenColumnsHelper';
import DocumentSignatureRequestModal from '@/shared/components/DocumentSignatureRequestModal';
import SharepointModal from '@/modules/documents/modals/SharepointModal';
import { ExtractZipModal } from '@/modules/documents/modals/ExtractZipModal';
import LinkFromSharepointModal from '@/modules/documents/modals/LinkFromSharepointModal';
import CollapsableSidebar from '@/shared/components/CollapsableSidebar';
import { getRelationName } from '@/shared/components/RelationName';
import CreateToDoModal from '../tasks/CreateToDoModal';
import UpdateDocumentRelationsModal from './modals/UpdateDocumentRelationsModal';
import UpdateDocumentTagsModal from './modals/UpdateDocumentTagsModal';
import DocumentGlobalActionsButton from './DocumentGlobalActionsButton';
import DocumentFilters, { defaultFilters } from './DocumentFilters';
import LoadingAnimation from '../../shared/components/LoadingAnimation';
import { containsDragFormat } from '../../shared/utils/dragEventHelpers';
import DocumentDetailsModal from './details/DocumentDetailsModal';
import Dropzone from './Dropzone';
import DocumentRow from './DocumentRow';
import messages from './documentMessages';
import ChangeDocumentDateModal from './modals/ChangeDocumentDateModal';
import ChangeSigningStatusModal from './modals/ChangeSigningStatusModal';
import ChangeDocumentTypeModal from './modals/ChangeDocumentTypeModal';
import SetIsPublicOnPortalModal from './modals/SetIsPublicOnPortalModal';
import ProcessAuthorizationListModal from './ProcessAuthorizationListModal';
import ProcessRegistrationListModal from './ProcessRegistrationListModal';
import LinkRelationDocumentModal from './LinkRelationDocumentModal';
import config from '../../config';
import DownloadDocumentZipModal from './modals/DownloadDocumentZipModal';
import AdvancedUploadModal from './details/AdvancedUploadModal';
import SelectRelationModal from './modals/SelectRelationModal';
import ForcedRelationMode from './ForcedRelationMode';
import AuthService from '@/shared/services/AuthService';
import CheckoutReminderMode from './CheckoutReminderMode';
import { actions } from '@/shared/components/registration/registrationHelpers';

const columnSelectionStorageKey = 'DocumentOverviewHiddenColumns';
const columnDefinitions = [
  { id: 'documentType', message: messages.Type, visibleByDefault: true },
  { id: 'relation', message: messages.Relation, visibleByDefault: true },
  { id: 'recognitionStatus', message: messages.Status, visibleByDefault: true },
  { id: 'documentDate', message: messages.DocumentDate, visibleByDefault: true },
  { id: 'creationDate', message: messages.CreationDate, visibleByDefault: true },
  { id: 'createdBy', message: messages.CreatedBy, visibleByDefault: false },
];

const tagsToString = tags => (tags ? tags.sort().join(';') : null);

const dontShowAgainStorageKeyDocuments = 'DocumentsAIExtractionWarningDontShowAgain';

class DocumentsContainer extends Component {
  state = {
    documents: [],
    documentsThatShouldBeHidden: [],
    showPickRelationModal: false,
    showDocumentDetailsModal: false,
    showProcessAuthorizationListModal: false,
    showProcessYukiRegistrationListModal: false,
    initialSearchParams: {},
    treeviewReady: false,
    allSelected: false,
    showUpdateTagsModal: false,
    showUpdateRelationsModal: false,
    showRemoveBatchDocumentsModal: false,
    showDownloadZipModal: false,
    showCreateTodoModal: false,
    showSetPublicOnPortalModal: false,
    showChangeDocumentDateModal: false,
    showChangeSigningStatusModal: false,
    showChangeDocumentTypeModal: false,
    showColumnSelectionModal: false,
    showMoveToSharepointModal: false,
    showLinkFromSharepointModal: false,
    showExtractZipModal: false,
    showExtractDataConfirmModal: false,
    actionsInProgress: false,
  };

  constructor(props) {
    super(props);
    const {
      searchParams: { mainFilters: searchParamsFilters = {}, ...otherSearchParams } = {},
      intl,
      documentId,
    } = props;

    let mainFilters = { ...defaultFilters };
    if (searchParamsFilters && Object.keys(searchParamsFilters).length) {
      mainFilters = { ...mainFilters, ...searchParamsFilters };
    } else {
      const storageFilters = userStorage.getItem('DocumentsOverviewFilters') || {};

      if (storageFilters.dateFrom) {
        storageFilters.dateFrom = moment.utc(storageFilters.dateFrom);
      }
      if (storageFilters.dateUntil) {
        storageFilters.dateUntil = moment.utc(storageFilters.dateUntil);
      }

      mainFilters = { ...mainFilters, ...storageFilters };
    }

    const hiddenColumnsState = initHiddenColumnsState(
      columnSelectionStorageKey,
      columnDefinitions,
      intl,
    );

    this.state = {
      ...this.state,
      ...hiddenColumnsState,
      initialSearchParams: {
        sortKey: mainFilters?.sort ?? 'creationDate',
        desc: mainFilters?.desc ?? true,
        pageSize: 50,
        mainFilters,
        ...otherSearchParams,
      },
    };

    if (documentId) {
      this.state.showDocumentDetailsModal = true;
      this.state.activeDocument = { id: documentId };
    }

    this.availableActions = [
      {
        actionType: 0,
        action: 'marksigned',
        label: intl.formatMessage(messages.MarkAsSigned),
        msgSuccess: intl.formatMessage(messages.MsgMarkSignedLetterSuccess),
        msgError: intl.formatMessage(messages.MsgMarkSignedLetterError),
        func: document => this.handleRecognizedDocumentAction(0, document),
      },
      {
        actionType: 1,
        action: 'createrelation',
        label: intl.formatMessage(messages.CreateRelation),
        msgSuccess: intl.formatMessage(messages.MsgCreateRelationSuccess),
        msgError: intl.formatMessage(messages.MsgCreateRelationError),
        func: document => this.handleRecognizedDocumentAction(1, document),
      },
      {
        actionType: 2,
        action: 'updaterelation',
        label: intl.formatMessage(messages.UpdateRelation),
        msgSuccess: intl.formatMessage(messages.MsgUpdateRelationSuccess),
        msgError: intl.formatMessage(messages.MsgUpdateRelationError),
        func: document => this.handleRecognizedDocumentAction(2, document),
      },
      {
        actionType: 3,
        action: 'link',
        label: intl.formatMessage(messages.LinkToRelation),
        msgSuccess: intl.formatMessage(messages.MsgLinkRelationDocumentSuccess),
        msgError: intl.formatMessage(messages.MsgLinkRelationDocumentError),
        func: document => this.openLinkRelationModal(document),
      },
      {
        actionType: 4,
        action: 'open',
        label: intl.formatMessage(messages.Open),
        func: document =>
          this.setState({ showDocumentDetailsModal: true, activeDocument: document }),
        icon: <EyeIcon className="margin5R" />,
      },
      {
        actionType: 6,
        action: 'showall',
        label: intl.formatMessage(messages.ShowAllDocumentsForRelation),
        func: document => this.filterDocumentsByRelation(document.relations[0]),
        icon: <ListIcon className="margin5R" />,
      },
      {
        actionType: 7,
        action: 'updateauthorizations',
        label: intl.formatMessage(messages.UpdateAuthorizations),
        func: document => this.openProcessAuthorizationListModal(document),
      },
      {
        actionType: 8,
        action: 'importregistrationlist',
        label: intl.formatMessage(messages.Importregistrationlist),
        func: document => this.importRegistrationListHandler(document),
        disabled: document => document.recognitionStatus === 3,
      },
      {
        actionType: 9,
        action: 'extractZip',
        label: intl.formatMessage(messages.ExtractZip),
        func: document => this.handleExtractZip(document),
        disabled: document => document.recognitionStatus === 3,
      },
      {
        actionType: 10,
        action: 'createrelationfromrelationimportmodel',
        label: intl.formatMessage(messages.CreateRelation),
        msgSuccess: intl.formatMessage(messages.MsgCreateRelationSuccess),
        msgError: intl.formatMessage(messages.MsgCreateRelationError),
        func: document => this.handleRecognizedDocumentAction(10, document),
      },
    ];

    this.availableActions = this.availableActions.map(action => {
      if (!action.disabled) {
        // eslint-disable-next-line no-param-reassign
        action.disabled = () => false;
      }
      return action;
    });

    this.withoutRelationDocumentTypes = [];
    this.handleExtractZipAction = this.handleExtractZipAction.bind(this);
  }

  static propTypes = {
    intl: intlShape.isRequired,
    searchParams: PropTypes.shape({}),
  };

  static defaultProps = {
    searchParams: {},
  };

  documentTagAccessUpdatedHandler = tagAccessInfo => {
    if (
      tagAccessInfo.noOneHasAccess ||
      (tagAccessInfo.usersWithAccess?.length > 0 &&
        tagAccessInfo.usersWithAccess.indexOf(AuthService.getUserId()) < 0)
    ) {
      this.setState(({ documents, documentsThatShouldBeHidden }) => ({
        documents: documents.filter(document => document.id !== tagAccessInfo.documentId),
        documentsThatShouldBeHidden: [...documentsThatShouldBeHidden, tagAccessInfo.documentId],
      }));
    }
  };

  documentUpdateHandler = (document, showToastrMessages) => {
    // use 500ms delay because the signalr event of the document recognition might be faster than
    // the setState method that adds a new document to the documents state collection
    // if this happens, the document's properties are not updated because it is not yet found in the state collection
    // -> upload doc -> add doc to state (takes 200 ms) -> simultaneous signalr event of document -> document not found in state
    setTimeout(this.onDocumentUpdated(document, showToastrMessages), 500);
  };

  documentsUpdateHandler = documents => {
    if (documents && documents.length) {
      documents.map(document => this.documentUpdateHandler(document, false));
    }
  };
  documentDeleteHandler = documentId => {
    this.setState(({ documents }) => ({
      documents: documents.filter(document => document.id !== documentId),
    }));
  };

  documentMovedToSharePointHandler = documentId => {
    const { intl } = this.props;
    ToastrService.success(intl.formatMessage(messages.SharePointFileMovedInBackground));

    this.setState(({ documents }) => ({
      documents: documents.map(x => (x.id === documentId ? { ...x, storageType: 1 } : x)),
    }));
  };

  documentCheckedoutHandler = data => {
    const { intl } = this.props;

    // Handle error object with ErrorCode
    if (data && typeof data === 'object' && data.ErrorCode) {
      const type = data.Type || 'connectexceptioncode';
      ToastrService.error(intl.formatMessage({ id: `${type}_${data.ErrorCode}` }));
      this.setState({ actionsInProgress: false });
      return;
    }

    const documentEditLink = typeof data === 'object' ? data.EditLink : data;
    ToastrService.success(intl.formatMessage(messages.CheckedOutSuccesfull));
    this.setState({ actionsInProgress: false });
    DocumentService.tryOpenUrl(documentEditLink);
  };

  documentCheckedinHandler = documentId => {
    const { intl } = this.props;
    const { documents } = this.state;
    const document = documents.find(x => x.id === documentId);
    this.setState({ actionsInProgress: false });
    this.updateDocumentInState({ ...document, checkedOutByUserId: null });
  };

  documentDiscardCheckoutHandler = documentId => {
    const { intl } = this.props;
    ToastrService.success(intl.formatMessage(messages.MsgDiscardCheckoutDocumentSuccess));
    this.setState({ actionsInProgress: false });
  };

  documentExtractionCompletedHandler = result => {
    const { intl } = this.props;
    const { documents } = this.state;

    const document = documents.find(x => x.id === result.documentId);
    if (!document) return;

    this.updateDocumentInState({
      ...document,
      extracting: false,
    });

    if (result.success) {
      if (result.code === 18002) {
        ToastrService.info(intl.formatMessage(messages.DocumentDataExtractionInsufficientData));
      } else if (result.code === 18003) {
        ToastrService.info(intl.formatMessage(messages.DocumentDataExtractionUnsupportedType));
      } else {
        ToastrService.success(intl.formatMessage(messages.DocumentDataExtracted));
      }
    } else {
      const message = intl.formatMessage({
        id: `exceptioncode_${result.code}`.toLowerCase(),
        defaultMessage: intl.formatMessage(messages.DocumentDataExtractionFailed),
      });
      ToastrService.error(message);
    }
  };

  onExtractDocumentData = activeDocument => {
    const { intl } = this.props;

    if (!activeDocument.relations || activeDocument.relations.length === 0) {
      ToastrService.warning(intl.formatMessage(messages.NoRelationForExtraction));
      return;
    }

    const skipConfirmation = localStorage.getItem(dontShowAgainStorageKeyDocuments) === 'true';

    if (skipConfirmation) {
      this.performDocumentExtraction(activeDocument);
    } else {
      this.setState({
        showExtractDataConfirmModal: true,
        activeDocument,
      });
    }
  };

  performDocumentExtraction = activeDocument => {
    const { intl } = this.props;

    this.updateDocumentInState({ ...activeDocument, extracting: true });

    DocumentService.extractDocumentData(activeDocument.id).then(
      () => {
        ToastrService.info(intl.formatMessage(messages.DocumentDataExtractionStarted));
      },
      error => {
        this.updateDocumentInState({ ...activeDocument, extracting: false });
        handleHttpError(intl)(error);
      },
    );
  };

  async componentDidMount() {
    const { intl } = this.props;
    SignalRService.subscribe('documentUpdated', this.documentUpdateHandler);
    SignalRService.subscribe('documentTagAccessUpdated', this.documentTagAccessUpdatedHandler);
    SignalRService.subscribe('documentsUpdated', this.documentsUpdateHandler);
    SignalRService.subscribe('documentDeleted', this.documentDeleteHandler);
    SignalRService.subscribe('moveToSharePointFinished', this.documentMovedToSharePointHandler);
    SignalRService.subscribe('checkoutFinished', this.documentCheckedoutHandler);
    SignalRService.subscribe('checkinSuccesful', this.documentCheckedinHandler);
    SignalRService.subscribe('checkoutDiscarded', this.documentDiscardCheckoutHandler);
    SignalRService.subscribe(
      'documentExtractionCompleted',
      this.documentExtractionCompletedHandler,
    );

    const documentTypes = await enumCache.getTranslated('documentType', intl, false);
    this.setState({ documentTypes });

    DocumentService.getWithoutRelationDocumentTypes().then(types => {
      this.withoutRelationDocumentTypes = types;
    });
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('documentUpdated', this.documentUpdateHandler);
    SignalRService.unsubscribe('documentTagAccessUpdated', this.documentTagAccessUpdatedHandler);
    SignalRService.unsubscribe('documentsUpdated', this.documentsUpdateHandler);
    SignalRService.unsubscribe('documentDeleted', this.documentDeleteHandler);
    SignalRService.unsubscribe('moveToSharePointFinished', this.documentMovedToSharePointHandler);
    SignalRService.unsubscribe('checkoutFinished', this.documentCheckedoutHandler);
    SignalRService.unsubscribe('checkinSuccesful', this.documentCheckedinHandler);
    SignalRService.unsubscribe('checkoutDiscarded', this.documentDiscardCheckoutHandler);
    SignalRService.unsubscribe(
      'documentExtractionCompleted',
      this.documentExtractionCompletedHandler,
    );

    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  onDocumentUploaded = (error, document, meta) => {
    const { intl } = this.props;
    const { initialSearchParams, documents, documentsThatShouldBeHidden } = this.state;
    if (error) {
      log.error(error);

      if (error.body) {
        const translatedError = {
          type: 'documentdomainexceptioncode',
          code: error.body,
        };

        handleHttpError(intl)(translatedError);
      } else if (error.code) {
        handleHttpError(intl)(error);
      }

      return;
    }

    // the list might already contain the document, if the signalr message was faster than this response
    const alreadyContainsDocument = documents.some(x => x.id === document.id);
    if (alreadyContainsDocument) return;

    const { params: metaParams } = meta || {};
    if (initialSearchParams.tags && metaParams && metaParams.tags !== initialSearchParams.tags)
      return;
    if (documentsThatShouldBeHidden.includes(document.id)) return;

    this.setState(state => ({
      ...state,
      documents: [document, ...state.documents],
    }));
  };

  onUploadDocument = ({ files, node }) => {
    const canCreate = authService.hasPermission({
      functionality: 'documents',
      privilege: privileges.Create,
    });
    if (!canCreate) return;

    const { pond } = this;
    const { initialSearchParams } = this.state;
    if (!pond) return;
    const params = { tags: node ? tagsToString(node.tags) : null };

    if (initialSearchParams) {
      if (!params.tags && initialSearchParams.tags) {
        params.tags = initialSearchParams.tags;
      }
      if (initialSearchParams.mainFilters && initialSearchParams.mainFilters.relationId) {
        params.linkId = initialSearchParams.mainFilters.relationId;
        params.documentLinkType = 3;
      }
    }

    const filePondFiles = files.map(x => ({
      source: x,
      options: {
        metadata: { params },
      },
    }));

    pond.addFiles(filePondFiles);
  };

  deleteDocument(id) {
    const { documents } = this.state;
    const { intl } = this.props;

    return api.base.delete(`${config.appUrls.documents}/api/documents/${id}`).then(
      () => {
        ToastrService.success(intl.formatMessage(messages.MsgDeleteDocumentSuccess));
        this.setState(state => {
          const closeDetailModal = state.showDocumentDetailsModal && state.activeDocument.id === id;

          return {
            documents: documents.filter(document => document.id !== id),
            activeDocument: closeDetailModal ? null : state.activeDocument,
            showDocumentDetailsModal: closeDetailModal ? false : state.showDocumentDetailsModal,
            showSingleDeleteDocumentModal: false,
          };
        });
      },
      error => {
        console.error(error);
        if (error.response && error.response.data && error.response.data.code) {
          handleHttpError(intl)(error);
        } else {
          ToastrService.error(intl.formatMessage(messages.MsgDeleteDocumentError));
        }
        log.error('error deleting document ', error);
      },
    );
  }

  checkinDocument(document) {
    const { intl } = this.props;
    this.setState({ actionsInProgress: true });
    DocumentService.checkin(document.id).then(
      () => {
        this.setState({ showDiscardCheckoutModal: false });
      },
      error => {
        console.error(error);
        if (error.response && error.response.data && error.response.data.code) {
          handleHttpError(intl)(error);
        }
        this.setState({ showDiscardCheckoutModal: false, actionsInProgress: false });
        log.error('error checking in document ', error);
      },
    );
  }

  checkoutDocument = (document, openinApp) => {
    if (!document || !document.canEdit) return;
    const { intl } = this.props;
    this.setState({ actionsInProgress: true });
    DocumentService.checkout(document.id, openinApp).then(
      data => {
        if (!!data) {
          this.setState({ actionsInProgress: false });
          this.updateDocumentInState({
            ...document,
            checkedOutByUserId: AuthService.getUserId(),
            checkedOutDocumentLink: data,
          });
        }
      },
      error => {
        console.error(error);
        if (error.response && error.response.data && error.response.data.code) {
          handleHttpError(intl)(error);
        } else {
          ToastrService.error(intl.formatMessage(messages.MsgCheckoutDocumentSuccess));
        }
        log.error('error checking out document ', error);
        this.setState({ actionsInProgress: false });
      },
    );
  };

  discardCheckout = document => {
    const { intl } = this.props;
    DocumentService.discardCheckout(document.id).then(
      () => {
        this.updateDocumentInState({ ...document, checkedOutByUserId: null });
        this.setState({ showDiscardCheckoutModal: false });
      },
      error => {
        console.error(error);
        if (error.response && error.response.data && error.response.data.code) {
          handleHttpError(intl)(error);
        }
        this.setState({ showDiscardCheckoutModal: false, actionsInProgress: false });
        log.error('error checking in document ', error);
      },
    );
  };

  loadRecognitionMeta = document => {
    if (typeof document.recognitionMeta !== 'undefined')
      return Promise.resolve(document.recognitionMeta);
    return api.base
      .get(`${config.appUrls.documents}/api/documents/RecognitionMeta/${document.id}`)
      .then(({ data }) => {
        this.onDocumentUpdated({
          ...document,
          recognitionMeta: data || {},
        });

        return data || {};
      });
  };

  handleExtractZip(document) {
    this.setState(state => ({
      ...this.state,
      activeDocument: document,
      showExtractZipModal: true,
    }));
  }

  async handleExtractZipAction(action) {
    await this.handleRecognizedDocumentAction(9, this.state.activeDocument, action);
    this.setState(state => ({
      ...this.state,
      activeDocument: undefined,
      showExtractZipModal: false,
    }));
  }

  handleRecognizedDocumentAction(action, document, jsonData = null) {
    this.onDocumentUpdated({
      ...document,
      saving: true,
    });

    let json = null;
    if (jsonData) {
      if (typeof jsonData === 'string') json = jsonData;
      if (typeof jsonData === 'object') json = JSON.stringify(jsonData);
    }

    return api.base
      .post(`${config.appUrls.api}/api/documentrecognition/HandleDocumentAction`, {
        action,
        documentId: document.id,
        json,
      })
      .then(
        ({ data }) => {
          if (!data) return;
          ToastrService.success(
            this.availableActions.find(x => x.actionType === action).msgSuccess,
          );
          this.onDocumentUpdated({
            ...data,
            saving: false,
          });
        },
        error => {
          log.error(error);
          let message = this.availableActions.find(x => x.actionType === action).msgError;
          const err = error.response.data;
          if (typeof err === 'object' && err.code > 0) {
            // TODO: Show actual message for code
            message += ` (${err.code})`;
          }
          ToastrService.error(message);

          this.onDocumentUpdated({
            ...document,
            saving: false,
          });
        },
      );
  }

  importRegistrationListHandler(document) {
    const handleRegistrationList = documentId => {
      const { intl, stateService } = this.props;
      const model = { documentId };

      return api.base
        .post(`${config.appUrls.api}/api/documentrecognition/HandleRegistrationList`, model)
        .then(
          ({ data }) => {
            const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
            ToastrService.info(
              intl.formatMessage(messages.ProcessRegistrationListQueued),
              null,
              onClick,
            );
          },
          error => {
            handleHttpError(intl)(error);
          },
        );
    };

    this.loadRecognitionMeta(document).then(meta => {
      if (!meta || meta.registrationListType === 0) {
        // 'Normal' registration list, no need to ask for a reference date
        handleRegistrationList(document.id);
      } else {
        // 'Yuki' registration import requiring asking for a reference date
        this.setState(state => ({
          ...state,
          activeDocument: document,
          showProcessYukiRegistrationListModal: true,
        }));
      }
    });
  }

  openLinkRelationModal(document) {
    this.setState(state => ({
      ...state,
      activeDocument: document,
      showPickRelationModal: true,
    }));
  }

  openProcessAuthorizationListModal(document) {
    this.setState(state => ({
      ...state,
      activeDocument: document,
      showProcessAuthorizationListModal: true,
    }));
  }

  filtersChanged(filters) {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        mainFilters: { ...state.initialSearchParams.mainFilters, ...filters },
      },
    }));
  }

  resetFilters = () => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        tags: '',
        mainFilters: { ...defaultFilters },
      },
    }));
  };

  filterDocumentsByRelation(relation) {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        mainFilters: {
          ...state.initialSearchParams.mainFilters,
          relationId: relation.id,
          relationName: relation.name,
        },
        page: 0,
      },
    }));
  }

  filterDocumentsByTags = (tags, force) => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        tags: tagsToString(tags),
        page: 0,
        f: force ? new Date().getTime() : undefined,
      },
    }));
  };

  loadDocuments = (
    { tags, desc, sortKey: sort, page, pageSize: take, mainFilters = {} },
    requestNr,
  ) => {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    this.cancelToken = Axios.CancelToken.source();
    this.lastRequestNr = requestNr;

    const { relationId = null, ...rest } = mainFilters;

    const params = {
      ...rest,
      desc,
      sort,
      page,
      take,
      tags,
    };

    if (relationId) {
      params.linkId = relationId;
      params.linkType = 3; // link type 3 = relation
    }

    const storageFilters = { ...mainFilters, sort, desc };

    userStorage.setItem('DocumentsOverviewFilters', storageFilters);

    return api.base
      .post(`${config.appUrls.documents}/api/documents/GetAll`, params, {
        cancelToken: this.cancelToken.token,
      })
      .then(({ data }) => {
        if (this.lastRequestNr !== requestNr) return;
        const { allSelected } = this.state;
        const newDocuments = data.documents.map(e => ({ ...e, selected: !!allSelected }));

        this.setState(state => ({
          ...state,
          cannotBeChangedTo: data.cannotBeChangedTo,
          documents: page > 0 ? [...state.documents, ...newDocuments] : [...newDocuments],
          currentSearchParams: {
            mainFilters,
            ...params,
          },
        }));

        return data.documents;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) {
          if (page <= 0 && this.lastRequestNr === requestNr) {
            this.setState({ documents: [] });
          }
          return error;
        }
        // Return empty array for cancelled requests so ScrollableTable can handle it properly
        return [];
      });
  };

  getPreviousDocumentId = documentId => {
    const { documents } = this.state;

    if (!documents) return;

    const currentIndex = documents.map(x => x.id).findIndex(x => x === documentId);

    // No previous doc present
    if (currentIndex <= 0) return;

    const previousIndex = currentIndex - 1;
    if (previousIndex < documents.length) {
      return documents[previousIndex].id;
    }
  };

  getNextDocument = documentId => {
    const { documents } = this.state;
    if (!documents) return null;
    const currentIndex = documents.map(x => x.id).findIndex(x => x === documentId);
    const nextIndex = currentIndex + 1;
    if (nextIndex < documents.length) {
      return documents[nextIndex];
    }
    return null;
  };
  getNextDocumentId = documentId =>
    new Promise((resolve, reject) => {
      const nextDocument = this.getNextDocument(documentId);
      if (nextDocument) {
        return resolve(nextDocument.id);
      }

      // Need to retrieve next page
      const { currentSearchParams } = this.state;

      const params = {
        ...currentSearchParams,
        page: currentSearchParams.page + 1,
      };

      api.base.post(`${config.appUrls.documents}/api/documents/GetAll`, params).then(({ data }) => {
        const { documents: newDocuments } = data;
        if (!newDocuments || newDocuments.length === 0) {
          return reject();
        }

        this.setState(
          ({ documents }) => ({
            documents: params.page > 0 ? [...documents, ...newDocuments] : [...data],
            currentSearchParams: params,
          }),
          () => {
            const nextDocument2 = this.getNextDocument(documentId);
            if (nextDocument2) {
              resolve(nextDocument2.id);
            } else {
              reject();
            }
          },
        );
      }, reject);
    });

  getDocumentAction = document => {
    const actionForDocument = {
      suggestedAction: {
        func: documentToOpen =>
          this.setState({ showDocumentDetailsModal: true, activeDocument: documentToOpen }),
        label: 'Open',
      },
      otherActions: [],
    };
    // PROCESSED: default action is to download, others are delete & filter on relation if relation is present
    if (document.recognitionStatus === 4 || document.recognitionStatus === 0) {
      actionForDocument.suggestedAction = this.availableActions.find(x => x.action === 'open');

      // If the document has a relation, show action to filter all documents by relation
      if (document.relations && document.relations.length) {
        if (document.relations.length === 1)
          actionForDocument.otherActions.push(
            this.availableActions.find(x => x.action === 'showall'),
          );
      } else if (
        document.documentType !== 14 &&
        !this.withoutRelationDocumentTypes.includes(document.documentType)
      ) {
        if (DocumentService.supportsDataExtraction(document.documentType)) {
          actionForDocument.suggestedAction = this.availableActions.find(x => x.action === 'link');
          actionForDocument.otherActions.push(this.availableActions.find(x => x.action === 'open'));
        } else {
          actionForDocument.otherActions.push(this.availableActions.find(x => x.action === 'link'));
        }
      }
    }
    // RECOGNIZED: default is calculated in backend, other = delete
    if (document.recognitionStatus === 2 || document.recognitionStatus === 3) {
      actionForDocument.suggestedAction = this.availableActions.find(x => x.action === 'link');
      // TODO hardcode actions
      if (document.documentType === 10 || document.documentType === 87) {
        // EID
        if (document.relations && document.relations.length === 1) {
          actionForDocument.suggestedAction = this.availableActions.find(
            x => x.action === 'updaterelation',
          );
        } else if (!document.relations || document.relations.length === 0) {
          actionForDocument.suggestedAction = this.availableActions.find(
            x => x.action === 'createrelation',
          );
        }
      }
      if (document.hasRelationImportModel && document.documentType === 36) {
        // KBO Extract
        if (!document.relations || document.relations.length <= 1) {
          actionForDocument.suggestedAction = this.availableActions.find(
            x => x.action === 'createrelationfromrelationimportmodel',
          );
        }
      }
      if (document.documentType === 19) {
        actionForDocument.suggestedAction = this.availableActions.find(
          x => x.action === 'updateauthorizations',
        );
      }
      if (document.documentType === 124) {
        actionForDocument.suggestedAction = this.availableActions.find(
          x => x.action === 'extractZip',
        );
      }
      if (document.documentType === 1000) {
        actionForDocument.suggestedAction = this.availableActions.find(
          x => x.action === 'importregistrationlist',
        );
      }
      if (
        [11, 12, 24, 25, 26, 27, 29, 32].indexOf(document.documentType) >= 0 &&
        document.relations.length === 1
      ) {
        actionForDocument.suggestedAction = this.availableActions.find(
          x => x.action === 'marksigned',
        );
      }
      // User can download/delete it
      actionForDocument.otherActions = this.availableActions.filter(
        x => x.action === 'open' || x.action === 'delete',
      );
      if (document.relations && document.relations.length === 1) {
        actionForDocument.otherActions.push(
          this.availableActions.find(x => x.action === 'showall'),
        );
      }
    }

    // NOT RECOGNIZED:
    if (document.recognitionStatus === 5) {
      // if its not linked to relation yet, default is to link, or open if the document type should not be linked to a relation.
      const canLinkToRelation =
        document.documentType !== 14 &&
        !this.withoutRelationDocumentTypes.includes(document.documentType);
      if (!document.relations || !document.relations.length) {
        if (canLinkToRelation) {
          actionForDocument.suggestedAction = this.availableActions.find(x => x.action === 'link');
        } else {
          actionForDocument.suggestedAction = this.availableActions.find(x => x.action === 'open');
        }
        actionForDocument.otherActions = this.availableActions.filter(
          x => x.action === 'open' || x.action === 'delete',
        );
      } else {
        actionForDocument.suggestedAction = this.availableActions.find(x => x.action === 'open');
        actionForDocument.otherActions = this.availableActions.filter(
          x => x.action === 'delete' || (document.relations.length === 1 && x.action === 'showall'),
        );
      }
    }

    return actionForDocument;
  };

  onSendViaEmail = async (activeDocument, asPdf, activeRelation) => {
    const { intl } = this.props;
    if (!activeRelation) {
      if (activeDocument.relations.length === 0) {
        ToastrService.error(intl.formatMessage(messages.NotLinkedToARelation));
        return;
      }
      if (activeDocument.relations.length === 1) {
        [activeRelation] = activeDocument.relations;
      } else {
        this.setState({
          activeDocument,
          asPdf,
          showSelectRelationModal: true,
          activeRelations: activeDocument.relations,
        });
        return;
      }
    }

    const attachment = asPdf
      ? await DocumentService.convertAndSaveToPdf(activeDocument.id)
      : activeDocument;

    if (!attachment) {
      ToastrService.error(intl.formatMessage(messages.DocumentCouldNotBeConverted));
      return;
    }

    this.setState({
      showSendByEmailModal: true,
      selectedRelationId: activeRelation.id,
      selectedCompanyId: activeRelation.companyId,
      selectedDocuments: [attachment],
    });
  };

  linkDocumentToRelation = relationDocument => {
    const { relationId, documentId } = relationDocument;
    return this.handleRecognizedDocumentAction(3, relationDocument, {
      relationId,
      documentId,
    }).then(() =>
      this.setState(state => ({
        ...state,
        showPickRelationModal: false,
      })),
    );
  };

  handleSelection = document => {
    this.setState(({ documents }) => ({
      documents: documents.map(x => (x.id === document.id ? { ...x, selected: !x.selected } : x)),
    }));
  };

  renderRow = document => {
    const { intl } = this.props;
    const { columnStatus, cannotBeChangedTo, documentTypes, actionsInProgress } = this.state;
    const action = this.getDocumentAction(document);
    return (
      <DocumentRow
        key={document.id}
        document={document}
        intl={intl}
        onOpenDocumentDetails={activeDocument =>
          this.setState({ showDocumentDetailsModal: true, activeDocument })
        }
        columnStatus={columnStatus}
        action={action}
        handleSelection={this.handleSelection}
        loadRecognitionMeta={this.loadRecognitionMeta}
        onDeleteDocument={activeDocument =>
          this.setState({ showSingleDeleteDocumentModal: true, activeDocument })
        }
        onCreateTodo={activeDocument =>
          this.setState({ showCreateTodoModal: true, activeDocument })
        }
        onOpenSignatureRequestModal={activeDocument =>
          this.setState({ showSignatureRequestModal: true, selectedDocuments: [activeDocument] })
        }
        onOpenRevokeSignatureRequestModal={activeDocument =>
          this.setState({ showRevokeSignatureRequestModal: true, activeDocument })
        }
        onShareItem={activeDocument => this.setState({ showShareItemModal: true, activeDocument })}
        onDeleteRelation={relationId =>
          this.setState({
            showDeleteRelationLinkModal: true,
            activeDocument: document,
            activeRelationId: relationId,
          })
        }
        openInApp={this.openInApp}
        duplicate={this.duplicate}
        onSendViaEmail={this.onSendViaEmail}
        onForward={activeDocument => this.setState({ showForwardModal: true, activeDocument })}
        onExtractDocumentData={this.onExtractDocumentData}
        onUpdateDocumentDate={documentDate =>
          this.updateDocumentDate({ id: document.id, documentDate })
        }
        onUpdateDocumentType={documentType =>
          this.updateDocumentType({ id: document.id, documentType })
        }
        onSendToSharepoint={activeDocument =>
          this.setState({ showMoveToSharepointModal: true, activeDocument })
        }
        onCheckinDocument={activeDocument => {
          if (activeDocument.checkedOutByUserId != authService.getUserId()) {
            this.setState({
              showDiscardCheckoutModal: true,
              activeDocument,
              actionsInProgress: true,
            });
          } else {
            this.checkinDocument(activeDocument);
          }
        }}
        onDiscardCheckout={activeDocument => {
          this.setState({
            actionsInProgress: true,
            showDiscardCheckoutModal: true,
            activeDocument,
          });
        }}
        onCheckoutDocument={(activeDocument, openinApp) =>
          this.checkoutDocument(activeDocument, openinApp)
        }
        onReplaceDocument={this.replaceDocument}
        actionsInProgress={actionsInProgress}
      />
    );
  };

  handleListDragOver = e => {
    if (!e.dataTransfer) return;
    if (containsDragFormat(e, 'Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };
  handleListDrop = e => {
    if (!e.dataTransfer) return;
    try {
      if (containsDragFormat(e, 'Files')) {
        e.preventDefault();
        this.onUploadDocument({ files: [...e.dataTransfer.files] });
      }
    } catch (ex) {
      console.error(ex);
    }
  };

  replaceDocument = (document, [file]) => {
    if (!file || !document || !document.canBeReplaced) return;
    this.updateDocumentInState({ id: document.id, replacing: true });

    const formData = new FormData();
    formData.append('documentId', document.id);
    formData.append('data', file);

    return api.base
      .post(`${config.appUrls.documents}/api/Documents/Replace`, formData, {
        headers: {
          'Content-Type': undefined,
        },
      })
      .then(
        ({ data }) => {
          if (!data) return;
          ToastrService.success();

          this.updateDocumentInState({ ...data, replacing: false });
          return data;
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);

          return this.updateDocumentInState({ id: document.id, replacing: false });
        },
      );
  };

  updateDocumentInState = (updatedDocument, addIfNotExists) => {
    this.setState(state => {
      const { documents, documentsThatShouldBeHidden } = state;

      const index = documents.findIndex(x => x.id === updatedDocument.id);

      if (index !== -1) {
        const copy = [...documents];

        copy[index] = {
          ...copy[index],
          ...updatedDocument,
          filePath: updatedDocument.path || copy[index].filePath,
          signingStatus: updatedDocument.signingStatus,
        };
        return {
          documents: copy,
        };
      }
      if (!addIfNotExists || documentsThatShouldBeHidden.includes(updatedDocument.id))
        return { documents };
      return {
        documents: [updatedDocument, ...documents],
      };
    });
  };

  removeDocumentFromState = document => {
    this.setState(state => ({
      documents: state.documents.filter(d => d.id !== document.id),
    }));
  };

  onDocumentUpdated = (document, showToastrMessages = true, addIfNotExists = true) => {
    if (!document) return;
    const { initialSearchParams } = this.state;
    const { intl } = this.props;
    const { tags } = initialSearchParams;
    if (tags === null) {
      // all documents selected: can always update
      this.updateDocumentInState(document, addIfNotExists);
    }
    if (tags === '00000000-0000-0000-0000-000000000000') {
      // without tags selected: remove from list if document got a tag, otherwise update
      if (document.tags && document.tags.length) {
        if (showToastrMessages) ToastrService.info(intl.formatMessage(messages.DocumentWasMoved));
        this.removeDocumentFromState(document);
      } else {
        this.updateDocumentInState(document, addIfNotExists);
      }
    } else if (tags) {
      // document folder is selected, parse tags
      // if document does not contain all tags that are selected, remove from list since it was moved
      const selectedTags = tags.split(';');
      if (!document.tags) {
        this.updateDocumentInState(document, addIfNotExists);
      } else if (document.tags && document.tags.length) {
        const documentTags = document.tags.map(x => x.id);
        if (selectedTags.every(x => documentTags.some(y => y === x))) {
          this.updateDocumentInState(document, addIfNotExists);
        } else {
          if (showToastrMessages) ToastrService.info(intl.formatMessage(messages.DocumentWasMoved));
          this.removeDocumentFromState(document);
        }
      }
    }
  };
  onTreeviewLoaded = ({ tags }) => {
    this.setState(state => ({
      ...state,
      treeviewReady: true,
      initialSearchParams: {
        ...state.initialSearchParams,
        tags: tagsToString(tags),
        page: 0,
        f: new Date().getTime(),
      },
    }));
  };

  getSelectionCountFormatted() {
    const { allSelected, documents } = this.state;
    const { intl } = this.props;
    if (allSelected) {
      return intl.formatMessage(messages.AllDocumentsSelected);
    }

    const count = documents ? documents.filter(x => x.selected === true).length : 0;
    return intl.formatMessage(messages.CountDocumentsSelected, { count });
  }

  getSelectionCount() {
    const { allSelected, totalRows, documents } = this.state;

    const count = documents ? documents.filter(x => x.selected !== allSelected).length : 0;

    return allSelected ? totalRows - count : count;
  }

  handleSelectAll = () => {
    const { documents, allSelected } = this.state;

    const isCurrentlySelected = !allSelected;

    const documentsCopy = documents.map(e => ({ ...e, selected: !!isCurrentlySelected }));

    this.setState(state => ({
      ...state,
      allSelected: isCurrentlySelected,
      documents: documentsCopy,
    }));
  };

  isGlobalActionsDisabled = () => {
    const { documents, allSelected } = this.state;
    if (!documents || !documents.length) return true;
    if (documents.some(x => x.selected) || allSelected) return false;

    return true;
  };

  getSelectionParams = () => {
    const { documents, allSelected } = this.state;

    // Get all document ids that don't match the select all checkbox
    const documentIds = documents
      ? documents.filter(x => !!x.selected !== allSelected).map(x => x.id)
      : [];
    return {
      documentIds,
      selectAll: allSelected,
    };
  };

  executeBatchAction = ({ action, parameters }) => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl } = this.props;
    const { currentSearchParams } = this.state;
    const { stateService } = this.props;

    let endpoint = '';

    switch (action) {
      case 'updatetags':
        endpoint = 'UpdateTags';
        break;
      case 'updaterelations':
        endpoint = 'UpdateRelations';
        break;
      case 'removedocuments':
        endpoint = 'RemoveDocuments';
        break;
      case 'downloadzip': {
        const connectionId = SignalRService.getConnectionId();
        parameters = {
          ...parameters,
          signalRConnectionId: connectionId,
        };
        endpoint = 'DownloadZip';
        break;
      }
      case 'setispubliconportal':
        endpoint = 'SetIsPublicOnPortal';
        break;
      case 'createmergedpdf': {
        const connectionId = SignalRService.getConnectionId();
        parameters = {
          ...parameters,
          signalRConnectionId: connectionId,
        };
        endpoint = 'createmergedpdf';
        break;
      }
      default:
        endpoint = action;
        break;
    }

    const jobParameters = { ...parameters, ...this.getSelectionParams() };

    const url = `${config.appUrls.documents}/api/documentbatch/${endpoint}`;
    const allParams = {
      filterParams: currentSearchParams,
      ...jobParameters,
    };

    return api.base
      .post(url, allParams)
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showUpdateTagsModal: false,
          showUpdateRelationsModal: false,
          showRemoveBatchDocumentsModal: false,
          showDownloadZipModal: false,
          showSetPublicOnPortalModal: false,
          showChangeDocumentDateModal: false,
          showChangeSigningStatusModal: false,
          showChangeDocumentTypeModal: false,
        }));

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        if (action === 'ChangeSigningStatus') {
          ToastrService.success(intl.formatMessage(messages.Success));
        } else {
          ToastrService.info(intl.formatMessage(messages.GlobalActionStarted), undefined, onClick);
        }
      })
      .catch(handleHttpError(intl));
  };

  updateDocumentDate = viewModel => {
    const { intl } = this.props;
    if (!viewModel.documentDate) {
      ToastrService.error(intl.formatMessage(messages.InvalidDocumentDate));
      return;
    }
    return api.base
      .put(`${config.appUrls.documents}/api/documents/date`, viewModel)
      .then(() => {
        const { documents } = this.state;
        const originalDocument = documents.find(x => x.id === viewModel.id);
        const updatedDocument = {
          ...originalDocument,
          documentDate: viewModel.documentDate,
        };

        this.updateDocumentInState(updatedDocument);
        ToastrService.success(intl.formatMessage(messages.DocumentDateWasModified));
      })
      .catch(handleHttpError(intl));
  };

  updateDocumentType = viewModel => {
    const { intl } = this.props;

    return api.base
      .put(`${config.appUrls.documents}/api/documents/type`, viewModel)
      .then(response => {
        const updatedDocumentData = response.data;
        const { documents } = this.state;
        const originalDocument = documents.find(x => x.id === viewModel.id);
        const updatedDocument = {
          ...originalDocument,
          documentType: viewModel.documentType,
          isPublicOnPortal: updatedDocumentData.isPublicOnPortal,
        };

        this.updateDocumentInState(updatedDocument);
        ToastrService.success(intl.formatMessage(messages.Success));
      })
      .catch(handleHttpError(intl));
  };

  openEmailOrSignatureModal = emailOrSignatureRequest => {
    const { initialSearchParams, documents, allSelected } = this.state;
    const { intl } = this.props;

    if (allSelected) {
      ToastrService.error(intl.formatMessage(messages.PleaseMakeManualSelectionOfDocuments));
      return;
    }

    // if a relation was selected using the filters, this relation should be used to send the email
    let relationId =
      initialSearchParams &&
      initialSearchParams.mainFilters &&
      initialSearchParams.mainFilters.relationId;
    let companyId =
      initialSearchParams &&
      initialSearchParams.mainFilters &&
      initialSearchParams.mainFilters.companyId;

    if (relationId) {
      this.setState({
        selectedRelationId: relationId,
        selectedCompanyId: companyId,
        showSendByEmailModal: emailOrSignatureRequest === 'email',
        showSignatureRequestModal: emailOrSignatureRequest === 'signature',
        selectedDocuments: documents.filter(x => x.selected),
      });
      return;
    }

    // no relation was selected using the filters, check if the selected documents only belong to one relation.
    // if this is the case: the user can send an email to this relation too
    const selectedDocuments = documents && documents.filter(x => x.selected);

    if (selectedDocuments && selectedDocuments.length > 0) {
      const selectedRelations = selectedDocuments.flatMap(x => x.relations);
      const distinctRelations = [...new Set(selectedRelations.map(item => item.id))];
      if (distinctRelations && distinctRelations.length === 1) {
        // can mail document, since only documents for a single relation have been selected
        relationId = distinctRelations[0];
        companyId = selectedRelations.find(x => x.id === relationId).companyId;
        this.setState({
          selectedRelationId: relationId,
          selectedCompanyId: companyId,
          showSendByEmailModal: emailOrSignatureRequest === 'email',
          showSignatureRequestModal: emailOrSignatureRequest === 'signature',
          selectedDocuments: documents.filter(x => x.selected),
        });
        return;
      }
    }
    ToastrService.error(intl.formatMessage(messages.PleaseFilterOnRelationBeforeSendingEmail));
  };

  getSelectedCompany = () => {
    const { initialSearchParams } = this.state;
    let selectedCompany;
    if (hasMultipleCompanies()) {
      if (
        initialSearchParams &&
        initialSearchParams.mainFilters &&
        initialSearchParams.mainFilters.ownCompanies &&
        initialSearchParams.mainFilters.ownCompanies.length === 1
      ) {
        const dataCache = DataCacheService.getDataCache();
        if (dataCache) {
          selectedCompany = dataCache.company.find(
            x => x.id === initialSearchParams.mainFilters.ownCompanies[0],
          );
        }
      } else {
        selectedCompany = DataCacheService.getDefaultCompanyForUser();
      }
    }

    return selectedCompany;
  };

  revokeSignatureRequest = activeDocument => {
    const { intl } = this.props;

    const dataCache = DataCacheService.getDataCache();
    const company =
      dataCache.company.find(x => x.id === activeDocument.companyId) || dataCache.defaultcompany;
    const { documentSigningProvider } = company;

    if (documentSigningProvider === 0) {
      ToastrService.error(intl.formatMessage(messages.NoDocumentProviderConfigured));
      return;
    }

    let url = `${config.appUrls.documentSigning}/api/RevokeDocumentSigningRequest/${company.id}/${activeDocument.signingId}/`;

    if (activeDocument.signingPackageId) {
      url += `${activeDocument.signingPackageId}`;
    }
    return api.base.post(url).then(
      () => {
        ToastrService.success(intl.formatMessage(messages.SignatureRequestRevoked));
        this.setState(state => ({
          documents: state.documents.map(x => {
            if (x.id === activeDocument.id) {
              return {
                ...x,
                signingStatus: 3, // revoked
              };
            }
            return x;
          }),
          showRevokeSignatureRequestModal: false,
        }));
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  handleColumnSelection = items => {
    this.setState(({ hideableColumns }) => ({
      ...handleHiddenColumnsSelection(items, hideableColumns, columnSelectionStorageKey),
      showColumnSelectionModal: false,
    }));
  };

  handleExtractDataWithAI = () => {
    const skipConfirmation = localStorage.getItem(dontShowAgainStorageKeyDocuments) === 'true';

    if (skipConfirmation) {
      this.confirmExtractDataWithAI(false);
    } else {
      this.setState({
        showExtractDataConfirmModal: true,
      });
    }
  };

  confirmExtractDataWithAI = (dontShowAgain = false) => {
    const { activeDocument } = this.state;

    if (dontShowAgain) {
      localStorage.setItem(dontShowAgainStorageKeyDocuments, 'true');
    }

    this.setState({ showExtractDataConfirmModal: false });

    if (activeDocument) {
      return this.performDocumentExtraction(activeDocument);
    } else {
      return this.executeBatchAction(
        {
          action: 'ExtractDocumentData',
        },
        {},
      );
    }
  };

  cancelExtractDataWithAI = () => {
    this.setState({
      showExtractDataConfirmModal: false,
      activeDocument: null,
    });
  };

  documentMovedToSharepoint = result => {
    const { intl } = this.props;
    const { activeDocument } = this.state;
    if (result.movedInBackground) {
      ToastrService.info(intl.formatMessage(messages.SharePointFileWillBeMovedInBackground));

      this.setState({ showMoveToSharepointModal: false });
    } else {
      ToastrService.success('', intl.formatMessage(messages.SharepointFileSuccessfullyMoved));

      this.setState({ showMoveToSharepointModal: false }, () => {
        activeDocument.storageType = 1;
        this.updateDocumentInState(activeDocument);
      });
    }
  };

  documentLinkedToSharepoint = document => {
    const { documents } = this.state;
    this.setState({ showLinkFromSharepointModal: false, documents: [document, ...documents] });
  };

  openInApp = document =>
    api.base
      .get(`${config.appUrls.documents}/api/documents/${document.id}/openinapp`)
      .then(({ data }) => {
        window.open(data, '_blank');
      });

  duplicate = document =>
    api.base
      .post(`${config.appUrls.documents}/api/documents/${document.id}/duplicate`)
      .then(({ data }) => {
        this.setState({
          documents: [data, ...this.state.documents],
        });
      });

  render() {
    const {
      initialSearchParams,
      documents,
      showPickRelationModal,
      activeDocument,
      showDocumentDetailsModal,
      treeviewReady,
      showProcessAuthorizationListModal,
      showProcessYukiRegistrationListModal,
      allSelected,
      showUpdateTagsModal,
      showUpdateRelationsModal,
      showRemoveBatchDocumentsModal,
      showSingleDeleteDocumentModal,
      showDownloadZipModal,
      showSendByEmailModal,
      showCreateTodoModal,
      showSetPublicOnPortalModal,
      showSignatureRequestModal,
      selectedRelationId,
      selectedCompanyId,
      showRevokeSignatureRequestModal,
      showShareItemModal,
      showDeleteRelationLinkModal,
      showChangeDocumentDateModal,
      showChangeSigningStatusModal,
      showChangeDocumentTypeModal,
      activeRelationId,
      showColumnSelectionModal,
      hideableColumns,
      columnStatus,
      showForwardModal,
      showAdvancedUploadModal,
      selectedDocuments,
      showSelectRelationModal,
      showMoveToSharepointModal,
      showExtractZipModal,
      activeRelations,
      asPdf,
      showLinkFromSharepointModal,
      documentTypes,
      cannotBeChangedTo,
      showDiscardCheckoutModal,
      showExtractDataConfirmModal,
    } = this.state;
    let documentUploadEndpoint = `${config.appUrls.documents}/api/documents?documentType=-1`; // -1 = document type unknown, used for recognizing

    const selectedCompany = this.getSelectedCompany();

    if (initialSearchParams) {
      if (initialSearchParams.tags && initialSearchParams.tags.length > 0) {
        documentUploadEndpoint += `&tags=${initialSearchParams.tags}`;
      }
      if (initialSearchParams.mainFilters && initialSearchParams.mainFilters.relationId) {
        documentUploadEndpoint += `&linkId=${initialSearchParams.mainFilters.relationId}&documentLinkType=3`;
      }
      if (selectedCompany) {
        documentUploadEndpoint += `&companyId=${selectedCompany.id}`;
      }
    }

    const relationName =
      initialSearchParams &&
      initialSearchParams.mainFilters &&
      initialSearchParams.mainFilters.relationName;

    const { intl, stateService } = this.props;
    return (
      <div className="documents-container">
        <ForcedRelationMode onDocumentSaved={document => this.onDocumentUpdated(document)} />
        <CheckoutReminderMode onDocumentSaved={document => this.onDocumentUpdated(document)} />

        {showMoveToSharepointModal && (
          <SharepointModal
            show
            documentId={activeDocument && activeDocument.id}
            intl={intl}
            onSave={data => this.documentMovedToSharepoint(data)}
            onClose={() => this.setState({ showMoveToSharepointModal: false })}
          />
        )}
        {showLinkFromSharepointModal && (
          <LinkFromSharepointModal
            show
            intl={intl}
            onSave={newDocument => this.documentLinkedToSharepoint(newDocument)}
            onClose={() => this.setState({ showLinkFromSharepointModal: false })}
            relationId={initialSearchParams?.mainFilters?.relationId}
            tags={initialSearchParams.tags}
          />
        )}
        {showSelectRelationModal && (
          <SelectRelationModal
            intl={intl}
            relations={activeRelations}
            show
            onClose={() => this.setState({ showSelectRelationModal: false })}
            onSave={relation => {
              this.setState({ showSelectRelationModal: false }, () =>
                this.onSendViaEmail(activeDocument, asPdf, relation),
              );
            }}
          />
        )}
        {showForwardModal && (
          <ForwardEmailModal
            intl={intl}
            documentId={activeDocument.id}
            relationId={
              activeDocument.relations && activeDocument.relations.length > 0
                ? activeDocument.relations[0].id
                : undefined
            }
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showForwardModal: false,
              }))
            }
          />
        )}
        {showSendByEmailModal && (
          <SingleRelationMailModal
            intl={intl}
            attachments={selectedDocuments}
            show
            relationId={selectedRelationId}
            companyId={selectedCompanyId}
            emailContext={13}
            onClose={() => this.setState({ showSendByEmailModal: false })}
            onSave={() => this.setState({ showSendByEmailModal: false })}
          />
        )}
        {showDocumentDetailsModal && (
          <DocumentDetailsModal
            show={showDocumentDetailsModal}
            document={activeDocument}
            getPreviousDocumentId={this.getPreviousDocumentId}
            getNextDocumentId={this.getNextDocumentId}
            onRemoveDocument={document =>
              this.setState({ activeDocument: document, showSingleDeleteDocumentModal: true })
            }
            onSave={document =>
              this.setState({ showDocumentDetailsModal: false }, () =>
                this.onDocumentUpdated(document, true, false),
              )
            }
            onClose={() =>
              this.setState({ showDocumentDetailsModal: false, activeDocument: undefined })
            }
            onCheckinCheckout={document => this.onDocumentUpdated(document)}
            onReplaceDocument={this.replaceDocument}
          />
        )}
        {showAdvancedUploadModal && (
          <AdvancedUploadModal
            show
            onSave={() => this.setState({ showAdvancedUploadModal: false })}
            onClose={() => this.setState({ showAdvancedUploadModal: false })}
          />
        )}
        {showProcessAuthorizationListModal && (
          <ProcessAuthorizationListModal
            show={showProcessAuthorizationListModal}
            documentId={activeDocument && activeDocument.id}
            onSave={() => this.setState({ showProcessAuthorizationListModal: false })}
            intl={intl}
            stateService={stateService}
            onClose={() =>
              this.setState({ showProcessAuthorizationListModal: false, activeDocument: undefined })
            }
          />
        )}
        {showProcessYukiRegistrationListModal && (
          <ProcessRegistrationListModal
            show={showProcessYukiRegistrationListModal}
            documentId={activeDocument && activeDocument.id}
            onSave={() => this.setState({ showProcessYukiRegistrationListModal: false })}
            intl={intl}
            stateService={stateService}
            onClose={() =>
              this.setState({
                showProcessYukiRegistrationListModal: false,
                activeDocument: undefined,
              })
            }
          />
        )}
        {showPickRelationModal && (
          <LinkRelationDocumentModal
            intl={intl}
            show={showPickRelationModal}
            document={activeDocument}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showPickRelationModal: false,
              }))
            }
            onSave={document => this.linkDocumentToRelation(document)}
          />
        )}
        {showUpdateTagsModal && (
          <UpdateDocumentTagsModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showUpdateTagsModal: false,
              }))
            }
            onSave={data =>
              this.executeBatchAction({
                action: 'updatetags',
                parameters: data,
              })
            }
          />
        )}
        {showUpdateRelationsModal && (
          <UpdateDocumentRelationsModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showUpdateRelationsModal: false,
              }))
            }
            onSave={data => {
              const parameters = {
                action: data.action,
                relations: data.relations.map(x => x.id),
              };
              return this.executeBatchAction({
                action: 'updaterelations',
                parameters,
              });
            }}
          />
        )}
        {showDeleteRelationLinkModal && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.DeleteRelationLink)}
            message={intl.formatMessage(messages.DeleteRelationLinkConfirmationMsg)}
            onNo={() => this.setState({ showDeleteRelationLinkModal: false })}
            onSave={() => {
              api.base
                .delete(
                  `${config.appUrls.documents}/api/documents/Links/${activeDocument.id}/${activeRelationId}/3`,
                )
                .then(
                  () => {
                    ToastrService.success(intl.formatMessage(messages.RelationLinkDeleted));
                    this.setState({
                      showDeleteRelationLinkModal: false,
                      documents: documents.map(x => {
                        if (x.id === activeDocument.id) {
                          return {
                            ...x,
                            relations: x.relations.filter(y => y.id !== activeRelationId),
                          };
                        }
                        return x;
                      }),
                    });
                  },
                  error => {
                    handleHttpError(intl)(error);
                  },
                );
            }}
          />
        )}
        {showRemoveBatchDocumentsModal && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.DeleteDocument)}
            message={intl.formatMessage(messages.DeleteMultipleDocumentsConfirmationMsg)}
            onNo={() => this.setState({ showRemoveBatchDocumentsModal: false })}
            onSave={() =>
              this.executeBatchAction({
                action: 'removedocuments',
                parameters: {},
              })
            }
          />
        )}
        {showSingleDeleteDocumentModal && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.DeleteDocument)}
            message={intl.formatMessage(messages.DeleteSingleDocumentConfirmationMsg)}
            onNo={() => this.setState({ showSingleDeleteDocumentModal: false })}
            onSave={() => this.deleteDocument(activeDocument.id)}
          />
        )}
        {showDownloadZipModal && (
          <DownloadDocumentZipModal
            intl={intl}
            show
            onSave={parameters =>
              this.executeBatchAction({
                action: 'downloadzip',
                parameters,
              })
            }
            onClose={() => this.setState({ showDownloadZipModal: false })}
          />
        )}
        {showSetPublicOnPortalModal && (
          <SetIsPublicOnPortalModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showSetPublicOnPortalModal: false,
              }))
            }
            onSave={data =>
              this.executeBatchAction({
                action: 'setispubliconportal',
                parameters: data,
              })
            }
          />
        )}
        {showChangeSigningStatusModal && (
          <ChangeSigningStatusModal
            intl={intl}
            show
            onClose={() => this.setState({ showChangeSigningStatusModal: false })}
            onSave={data =>
              this.executeBatchAction({
                action: 'ChangeSigningStatus',
                parameters: data,
              })
            }
          />
        )}
        {showChangeDocumentDateModal && (
          <ChangeDocumentDateModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showChangeDocumentDateModal: false,
              }))
            }
            onSave={data =>
              this.executeBatchAction({
                action: 'ChangeDocumentDate',
                parameters: data,
              })
            }
          />
        )}
        {showChangeDocumentTypeModal && (
          <ChangeDocumentTypeModal
            documentTypes={documentTypes}
            cannotBeChangedTo={cannotBeChangedTo}
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showChangeDocumentTypeModal: false,
              }))
            }
            onSave={data =>
              this.executeBatchAction({
                action: 'ChangeDocumentType',
                parameters: data,
              })
            }
          />
        )}
        {showExtractZipModal && (
          <ExtractZipModal
            onClose={() => this.setState({ showExtractZipModal: false, activeDocument: undefined })}
            onAccept={this.handleExtractZipAction}
          />
        )}
        {showCreateTodoModal && (
          <CreateToDoModal
            show
            documents={[activeDocument]}
            relationId={
              activeDocument.relations && activeDocument.relations.length > 0
                ? activeDocument.relations[0].id
                : undefined
            }
            onSave={() => this.setState({ showCreateTodoModal: false, activeDocument: undefined })}
            onClose={() => this.setState({ showCreateTodoModal: false, activeDocument: undefined })}
          />
        )}

        {showSignatureRequestModal && (
          <DocumentSignatureRequestModal
            show
            intl={intl}
            documents={selectedDocuments}
            onSave={updatedDocuments =>
              this.setState({
                showSignatureRequestModal: false,
                documents: documents.map(x => {
                  const updatedDocument = updatedDocuments.find(y => y.id === x.id);
                  if (updatedDocument) {
                    return { ...updatedDocument };
                  }
                  return x;
                }),
              })
            }
            onClose={() =>
              this.setState({ showSignatureRequestModal: false, activeDocument: undefined })
            }
          />
        )}
        {showRevokeSignatureRequestModal && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.RevokeSignatureRequest)}
            message={intl.formatMessage(messages.RevokeSignatureRequestConfirmationMsg)}
            onNo={() => this.setState({ showRevokeSignatureRequestModal: false })}
            onSave={() => this.revokeSignatureRequest(activeDocument)}
          />
        )}
        {showColumnSelectionModal && (
          <CheckboxListModal
            title={intl.formatMessage(messages.SelectOverviewColumns)}
            items={hideableColumns}
            onSave={this.handleColumnSelection}
            onClose={() => this.setState({ showColumnSelectionModal: false })}
            show={showColumnSelectionModal}
            sortByLabel={false}
          />
        )}
        {showShareItemModal && (
          <ShareItemModal
            show
            type="document"
            id={activeDocument.id}
            itemName={activeDocument.name}
            relationId={
              activeDocument.relations && activeDocument.relations.length > 0
                ? activeDocument.relations[0].id
                : undefined
            }
            relationName={
              activeDocument.relations && activeDocument.relations.length > 0
                ? getRelationName(intl, activeDocument.relations[0])
                : undefined
            }
            onClose={() => this.setState({ showShareItemModal: false })}
          />
        )}
        {showDiscardCheckoutModal && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.DiscardCheckout)}
            message={intl.formatMessage(messages.CheckinDocumentConfirmationMsg, {
              name: DataCacheService.getUserName(activeDocument.checkedOutByUserId),
            })}
            onNo={() => this.setState({ showDiscardCheckoutModal: false })}
            onSave={() => this.discardCheckout(activeDocument)}
          />
        )}
        {showExtractDataConfirmModal && (
          <YesNoModal
            show={showExtractDataConfirmModal}
            title={intl.formatMessage(messages.ConfirmExtractDataWithAI)}
            message={intl.formatMessage(messages.ConfirmExtractDataWithAIMessage)}
            onSave={dontShowAgain => this.confirmExtractDataWithAI(dontShowAgain)}
            onNo={this.cancelExtractDataWithAI}
            onClose={this.cancelExtractDataWithAI}
            showDontShowAgain={true}
          />
        )}
        <CollapsableSidebar>
          <div className="dropzone-container">
            <CanCreate on="documents">
              <Dropzone
                documentUploadEndpoint={documentUploadEndpoint}
                onDocumentUploaded={this.onDocumentUploaded}
                pondRef={ref => {
                  this.pond = ref;
                }}
                removeSuccessfullAfter={5000}
                allowDrop
                onAdvancedUpload={() => this.setState({ showAdvancedUploadModal: true })}
                onLinkFromSharepoint={() => {
                  this.setState({ showLinkFromSharepointModal: true });
                }}
              />
            </CanCreate>
            {selectedCompany && !relationName && (
              <div className="simple-panel">
                <div className="simple-panel-body">
                  <div className="document-relation-info">
                    <OverlayTrigger
                      rootClose
                      trigger={['hover', 'focus']}
                      placement="bottom"
                      overlay={
                        <Popover id="popover-trigger-hover-focus">
                          {intl.formatMessage(messages.AutoLinkDocumentsToCompanyExplanation)}
                        </Popover>
                      }
                    >
                      <span className="explanation-icon">
                        <FaInfoCircle />
                      </span>
                    </OverlayTrigger>
                    <FormattedMessage
                      {...messages.DocumentWillBeLinkedToCompanyAutomatically}
                      values={{ company: <b>{selectedCompany.name}</b> }}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="simple-panel">
              <div className="simple-panel-body">
                {relationName && (
                  <div className="document-relation-info">
                    <FormattedMessage
                      {...messages.DocumentWillBeLinkedToRelationAutomatically}
                      values={{ relation: <b>{relationName}</b> }}
                    />
                  </div>
                )}
                <DocumentTreeView
                  onFilterChanged={({ tags, force }) => this.filterDocumentsByTags(tags, force)}
                  onUploadDocument={this.onUploadDocument}
                  initialTags={initialSearchParams.tags}
                  onTreeLoaded={this.onTreeviewLoaded}
                />
              </div>
            </div>
          </div>
          <div className="documents-table-container">
            <SimplePanel article="5434843391506" title={intl.formatMessage(messages.Documents)}>
              <DocumentFilters
                filters={initialSearchParams.mainFilters}
                onFilterChange={filters => this.filtersChanged(filters)}
                onResetFilters={this.resetFilters}
                hasActiveTagFilter={!!initialSearchParams.tags}
                selectionCount={this.getSelectionCountFormatted()}
              />
            </SimplePanel>

            <div className="row btn-row hidden-xs">
              <div className="pull-right content-right">
                <Button
                  btnClassName="btn btn-xs btn-default margin10R"
                  onClick={() => this.setState({ showColumnSelectionModal: true })}
                >
                  <span className="margin5R">
                    <FaCog className="react-icon" />
                  </span>
                  <FormattedMessage {...messages.ColumnConfig} />
                </Button>
                <DocumentGlobalActionsButton
                  disabled={this.isGlobalActionsDisabled()}
                  onUpdateTags={() => this.setState({ showUpdateTagsModal: true })}
                  onUpdateRelations={() => this.setState({ showUpdateRelationsModal: true })}
                  onRemoveDocuments={() => this.setState({ showRemoveBatchDocumentsModal: true })}
                  onDownloadZip={() => this.setState({ showDownloadZipModal: true })}
                  onCreateMergePdf={() =>
                    this.executeBatchAction(
                      {
                        action: 'createmergedpdf',
                      },
                      {},
                    )
                  }
                  onSendByEmail={() => this.openEmailOrSignatureModal('email')}
                  onSendSignatureRequest={() => this.openEmailOrSignatureModal('signature')}
                  onSetIsPublicOnPortal={() => this.setState({ showSetPublicOnPortalModal: true })}
                  onChangeDocumentDate={() => this.setState({ showChangeDocumentDateModal: true })}
                  onChangeSigningStatus={() =>
                    this.setState({ showChangeSigningStatusModal: true })
                  }
                  onChangeDocumentType={() => this.setState({ showChangeDocumentTypeModal: true })}
                  onExtractDataWithAI={this.handleExtractDataWithAI}
                />
              </div>
            </div>

            <div
              className="dropzone dropable-files "
              dropinfo-files={intl.formatMessage(messages.UploadFilesToCurrentFolder)}
              onDragOver={e => this.handleListDragOver(e)}
              onDrop={e => this.handleListDrop(e)}
              style={{
                minHeight: '50vh',
              }}
            >
              {treeviewReady ? (
                <ScrollableTable
                  tableClassName="table-grid margin50B"
                  initialSearchParams={initialSearchParams}
                  items={documents}
                  noDataLabel={intl.formatMessage(messages.NoDocumentsFound)}
                  loadItems={(searchParams, requestNr) =>
                    this.loadDocuments(searchParams, requestNr)
                  }
                  renderRow={item => this.renderRow(item)}
                >
                  <ColumnHeader
                    title={
                      <Checkbox
                        extraClass="margin2L"
                        onClick={this.handleSelectAll}
                        value={allSelected}
                      />
                    }
                    className="hidden-xs"
                  />

                  <ColumnHeader
                    title={intl.formatMessage(messages.Name)}
                    className="file-name-column"
                    sortKey="fileName"
                  />
                  <ColumnHeader
                    title={intl.formatMessage(messages.Type)}
                    sortKey="documentType"
                    className="type-column"
                    visible={columnStatus.documentType}
                  />
                  <ColumnHeader
                    title={intl.formatMessage(messages.Relation)}
                    className="relation-column"
                    visible={columnStatus.relation}
                  />
                  <ColumnHeader
                    title={intl.formatMessage(messages.Status)}
                    sortKey="recognitionStatus"
                    className="status-column hidden-xs"
                    visible={columnStatus.recognitionStatus}
                  />
                  <ColumnHeader
                    title={intl.formatMessage(messages.DocumentDate)}
                    sortKey="documentDate"
                    className="document-date-column hidden-xs"
                    visible={columnStatus.documentDate}
                  />
                  <ColumnHeader
                    title={intl.formatMessage(messages.CreationDate)}
                    sortKey="creationDate"
                    className="creation-date-column hidden-xs"
                    visible={columnStatus.creationDate}
                  />
                  <ColumnHeader
                    title={intl.formatMessage(messages.CreatedBy)}
                    className="hidden-xs"
                    visible={columnStatus.createdBy}
                  />
                  <ColumnHeader title="" className="extra-icons-column hidden-xs" />

                  <ColumnHeader title="" className="actions-column hidden-xs" />
                </ScrollableTable>
              ) : (
                <LoadingAnimation />
              )}
            </div>
          </div>
        </CollapsableSidebar>
      </div>
    );
  }
}

export default showWhenPermission(
  'Documents',
  'read',
)(injectStateService(injectIntl(withDropZone(DocumentsContainer))));
